import { usePatientContext } from '@/components/Contexts/PatientContext';

import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import * as echarts from 'echarts/core';
import { useEffect, useRef, useState } from 'react';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
} from 'echarts/components';
import DateUtils from '@/utils/DateUtils';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import SimpleLink from '../SimpleLink';
import SvgUtils from '@/utils/pdfGenerator/SvgUtils';
console.log(SvgUtils);

// Register extensions
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  SVGRenderer,
  PieChart,
  ToolboxComponent,
]);

const E_HEIGHT = 400;
const E_WIDTH = 300;

const getHabitudesStatsChart = (
  vl: number,
  vi: number,
  vp: number,
  t,
  width: number,
  height: number,
  component: number,
  minPercent = '40%',
  maxPercent = '60%',
  fontWeight = 'bold',
  fontSize = '14'
) => {
  const option = {
    animation: true,
    tooltip: {
      trigger: 'item',
      formatter: '{b} {d}%',
    },
    color: ['#4c99c1', '#86abda', '#b6bdea'],
    grid: {
      left: '5%',
      right: '5%',
      bottom: '5%',
      top: '5%',
    },
    series: [
      {
        center: ['50%', '40%'],
        type: 'pie',
        id: 'vision',
        radius: [minPercent, maxPercent],
        data: [
          {
            name: 'VL',
            value: vl,
          },
          {
            name: 'VI',
            value: vi,
          },
          {
            name: 'VP',
            value: vp,
          },
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        label: {
          show: true,
          formatter(param: { name: string; percent: number }) {
            let name = t(param.name);
            // correct the percentage
            return name + '\n(' + Math.round(param.percent) + '%)';
          },
          position: 'outside',
          fontSize,
          fontWeight,
          overflow: 'none',
          width: 55,
        },
      },
    ],
    legend: {
      orient: 'vertical',
      show: true,
      left: 'center',
      bottom: '5%',
      formatter: function (name: string) {
        let newName = '';
        switch (name) {
          case 'VL':
            newName = t('Vision de loin');
            break;
          case 'VI':
            newName = t('Vision intermédiaire');
            break;
          case 'VP':
            newName = t('Vision de près');
            break;
        }
        return t(name) + ' : ' + newName;
      },
    },
  };

  // @ts-ignore
  const chart = echarts.init(component, 'light', {
    renderer: 'svg',
    width: width,
    height: height,
    ssr: component ? false : true,
  });
  chart.setOption(option);
  return chart;
};

const HabitudesStatsChart = ({
  language,
  vl,
  vi,
  vp,
}: {
  language: string;
  vl: number;
  vi: number;
  vp: number;
}) => {
  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: Math.max(E_HEIGHT, width * 0.25),
      });
    }
  }, [width]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current) {
      chart = getHabitudesStatsChart(
        vl,
        vi,
        vp,
        t,
        width,
        Math.max(E_HEIGHT, width * 0.25),
        svgRef.current
      );

      chartRef.current = chart;
    }
    return () => chart?.dispose();
  }, [vl, vi, vp, language]);

  const handleLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View onLayout={handleLayout} style={{ flex: 1, minHeight: 400 }}>
      <SvgChart ref={svgRef} />
      {Platform.OS === 'web' ? (
        <SimpleLink
          onPress={async () => {
            await SvgUtils.downloadPngFromSVG(
              chartRef.current?.getSvgDataURL(),
              width,
              Math.max(E_HEIGHT, width * 0.25),
              { x: 0, y: 0, width, height: Math.max(E_HEIGHT, width * 0.25), color: '#ffffff' },
              true,
              t('Vision_habitudes_stats_chart') + '_' + DateUtils.formatDate(new Date()) + '.png'
            );
          }}
        >
          Télécharger
        </SimpleLink>
      ) : null}
    </View>
  );
};

export default HabitudesStatsChart;
